import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { DataTable } from '@adornis/data-view/client/table/data-table.js';
import { View } from '@adornis/data-view/db/view.js';
import { guardDialog } from '@adornis/dialog/dialog-functions.js';
import { FieldConfiguration } from '@adornis/field-management/field-configuration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useMemo, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { GlobalConfigAPI } from '@martinus/global-config/api/GlobalConfigAPI.js';
import { SGConfig } from '@martinus/global-config/db/sg-config.js';
import { CardHeader } from '@adornis/components/client/CardHeader.js';
import { PageLoader } from '@martinus/styles/client/PageLoader.js';
import { html } from 'lit';
import { BillingPositionTemplateAPI } from '../../billingPositions/api/BillingPositionTemplateAPI.js';
import { BillingPositionTemplateEditDialog } from '../../billingPositions/client/BillingPositionTemplateEditDialog.js';
import { BillingPositionTemplate } from '../../billingPositions/entities/BillingPositionTemplate.js';
import { SgFormGridEntry } from '../../client/components/SgFormGridEntry.js';
import { SgSnackbar } from '../../client/components/ui/sg-snackbar.js';

export const SGAdminDashboardAccounting = stateful(() => {
  const { value: config, loading: configLoading } = useQuery(
    () => GlobalConfigAPI.subscribe()(selectionSet(() => SGConfig, 3)),
    [],
  );

  // the form edits a plain snapshot; `constructValue` turns it back into the entity before saving.
  // Built unconditionally so the hooks below keep their order across the loading render.
  const configData = useMemo(() => [(config?.toObject() ?? {}) as EntityData<SGConfig>], [config?._id]);

  const configForm = useForm(
    configData,
    {
      validator: null,
      onValuePicked: () => void saveConfig(),
    },
    [configData],
  );

  const saveConfig = async () => {
    const validationError = await configForm.validate();
    if (validationError) return;

    await guardDialog(async () => {
      await constructValue<SGConfig>(configForm.document).save();
    });
    SgSnackbar.show('Konfiguration gespeichert');
  };

  const [search, setSearch] = useState('');

  if (configLoading || !config) {
    return PageLoader({});
  }

  const dataViewOptions = {
    class: BillingPositionTemplate,
    // @ts-expect-error dataview cannot use narrowed entity at the moment it is only typed for complete entities
    operation: BillingPositionTemplateAPI.subscribe,
    view: new View({
      filter: new AdornisFilter({ searchFields: ['name', 'kuerzel'], search }),

      configuredColumns: [
        new FieldConfiguration({ fieldName: 'kuerzel', columnWidth: 'max-content' }),
        new FieldConfiguration({ fieldName: 'name' }),
        new FieldConfiguration({ fieldName: 'rhythm' }),
        new FieldConfiguration({ fieldName: 'priceInCents' }),
      ],
    }),
    selectionSet: selectionSet(() => BillingPositionTemplate, 1),
    entryClickHandler: entity => {
      void BillingPositionTemplateEditDialog(entity);
    },
  } satisfies DataViewOptions<typeof BillingPositionTemplate>;

  return html`
    <div class="card-container">
      <div class="card card-body flex-col">
        ${CardHeader({
          title: 'Buchhaltungskonstanten',
        })}
        <div class="grid grid-cols-[1fr_4fr] gap-3 items-center">
          ${SgFormGridEntry({
            label: 'Steuerberaternummer',
            render: TextInput({
              ...configForm.field('beraternummer'),
              placeholder: 'Keine Steuerberaternummer angegeben',
              placeholderMode: 'hide',
            }),
          })}
          ${SgFormGridEntry({
            label: 'Gläubiger-ID',
            render: TextInput({
              ...configForm.field('glauberID'),
              placeholder: 'Keine Gläubiger-ID angegeben',
              placeholderMode: 'hide',
            }),
          })}
        </div>
      </div>

      <div class="card card-body flex-col max-h-[calc(100vh-20rem)] overflow-y-auto">
        ${CardHeader({
          title: 'Abrechnungspositions-Templates',
        })}
        <div class="flex flex-row gap-2 items-center">
          ${TextInput({
            placeholder: 'Suchen...',
            value: search,
            clearable: true,
            onChanged: value => setSearch(value ?? ''),
          })}

          <button class="btn btn-success" @click=${() => BillingPositionTemplateEditDialog(null)}>
            <span class="icon-[material-symbols--add-rounded] size-5"></span>
            Abrechnungspositions-Template erstellen
          </button>
        </div>
        ${DataTable({ options: dataViewOptions })}
      </div>
    </div>
  `;
});
